(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const formatMoney = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'CAD'
    }).format(cents / 100);
  };

  const toast = (message) => {
    const region = qs('[data-toast-region]');
    if (!region || !message) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    region.appendChild(el);
    setTimeout(() => el.remove(), 3200);
  };

  const updateCartCount = (cart) => {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  };

  const renderDrawer = async () => {
    const response = await fetch(window.location.href, {headers: {'X-Requested-With': 'XMLHttpRequest'}});
    if (!response.ok) return;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = qs('[data-cart-drawer]', doc);
    const current = qs('[data-cart-drawer]');
    if (fresh && current) current.replaceWith(fresh);
  };

  const openDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    qs('[data-drawer-close]', drawer)?.focus();
  };

  const closeDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
  };

  document.addEventListener('click', async (event) => {
    const cardAddButton = event.target.closest('[data-card-add-to-cart]');
    if (cardAddButton) {
      event.preventDefault();
      event.stopPropagation();
      if (cardAddButton.disabled) return;

      const variantId = cardAddButton.dataset.variantId;
      if (!variantId) return;

      const originalLabel = cardAddButton.dataset.defaultLabel || cardAddButton.textContent;
      cardAddButton.disabled = true;
      cardAddButton.setAttribute('aria-busy', 'true');
      cardAddButton.textContent = '…';

      try {
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');

        const response = await fetch(window.FrostPeak.routes.cartAdd, {
          method: 'POST',
          headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body
        });

        if (!response.ok) {
          let message = document.documentElement.lang.startsWith('fr')
            ? 'Impossible d’ajouter ce produit.'
            : 'Unable to add this product.';
          try {
            const error = await response.json();
            message = error.description || error.message || message;
          } catch (_) {}
          throw new Error(message);
        }

        const cartResponse = await fetch(`${window.FrostPeak.routes.cart}.js`, {
          headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}
        });
        if (!cartResponse.ok) throw new Error('Cart update failed');
        const cart = await cartResponse.json();
        updateCartCount(cart);
        toast(window.FrostPeak.strings.added);

        if (qs('[data-cart-drawer]')) {
          openDrawer();
          await renderDrawer();
          openDrawer();
        }
      } catch (error) {
        toast(error.message);
      } finally {
        cardAddButton.disabled = false;
        cardAddButton.removeAttribute('aria-busy');
        cardAddButton.textContent = originalLabel;
      }
      return;
    }

    const cartTrigger = event.target.closest('[data-cart-open]');
    if (cartTrigger && qs('[data-cart-drawer]')) {
      event.preventDefault();
      openDrawer();
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      closeDrawer();
      return;
    }
    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      removeButton.disabled = true;
      const response = await fetch(window.FrostPeak.routes.cartChange, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({line: Number(removeButton.dataset.cartRemove), quantity: 0})
      });
      if (response.ok) {
        const cart = await response.json();
        updateCartCount(cart);
        await renderDrawer();
        openDrawer();
      }
      return;
    }
    const mobileOpen = event.target.closest('[data-mobile-menu-open]');
    if (mobileOpen) {
      const menu = qs('[data-mobile-menu]');
      menu?.setAttribute('aria-hidden', 'false');
      mobileOpen.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
      menu?.querySelector('[data-mobile-menu-close]')?.focus();
      return;
    }
    if (event.target.closest('[data-mobile-menu-close]')) {
      qs('[data-mobile-menu]')?.setAttribute('aria-hidden', 'true');
      qs('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'false');
      qsa('[data-filter-drawer]').forEach((drawer) => drawer.setAttribute('aria-hidden', 'true'));
      document.body.classList.remove('menu-open');
      document.body.classList.remove('filter-drawer-open');
      return;
    }
    const filter = event.target.closest('[data-mobile-filter]');
    if (filter) {
      const section = filter.closest('[data-catalog-section]') || document;
      const drawer = qs('[data-filter-drawer]', section);
      drawer?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('filter-drawer-open');
      qs('[data-filter-close]', drawer)?.focus();
      return;
    }

    const filterClose = event.target.closest('[data-filter-close]');
    if (filterClose) {
      const drawer = filterClose.closest('[data-filter-drawer]');
      drawer?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('filter-drawer-open');
      return;
    }

    const viewButton = event.target.closest('[data-catalog-view]');
    if (viewButton) {
      const section = viewButton.closest('[data-catalog-section]');
      const grid = qs('[data-catalog-grid]', section);
      const view = viewButton.dataset.catalogView;
      if (!grid || !view) return;
      grid.classList.toggle('is-list-view', view === 'list');
      qsa('[data-catalog-view]', section).forEach((button) => {
        button.setAttribute('aria-pressed', String(button === viewButton));
      });
      try { localStorage.setItem('frostpeak:catalog-view', view); } catch (_) {}
      return;
    }
    const copy = event.target.closest('[data-copy-link]');
    if (copy) {
      await navigator.clipboard.writeText(window.location.href);
      toast(copy.dataset.success || 'Lien copié');
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    if (event.submitter?.classList?.contains('shopify-payment-button__button')) return;
    event.preventDefault();
    const button = qs('[type="submit"]', form);
    const original = button?.textContent;
    if (button) {
      button.disabled = true;
      button.textContent = '…';
    }
    try {
      const response = await fetch(window.FrostPeak.routes.cartAdd, {
        method: 'POST',
        headers: {Accept: 'application/json'},
        body: new FormData(form)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erreur');
      }
      const cart = await fetch(`${window.FrostPeak.routes.cart}.js`).then((r) => r.json());
      updateCartCount(cart);
      toast(window.FrostPeak.strings.added);
      if (qs('[data-cart-drawer]')) {
        openDrawer();
        await renderDrawer();
        openDrawer();
      } else {
        window.location.href = window.FrostPeak.routes.cart;
      }
    } catch (error) {
      toast(error.message);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = original;
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
      qs('[data-mobile-menu]')?.setAttribute('aria-hidden', 'true');
      qs('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'false');
      qsa('[data-filter-drawer]').forEach((drawer) => drawer.setAttribute('aria-hidden', 'true'));
      qsa('[data-media-modal][open]').forEach((modal) => {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
      });
      document.body.classList.remove('menu-open');
      document.body.classList.remove('filter-drawer-open');
    }
  });

  qsa('[data-quantity]').forEach((wrapper) => {
    wrapper.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      const input = qs('input', wrapper);
      if (!button || !input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const max = Number(input.max || 999);
      const value = Number(input.value || min);
      input.value = Math.min(max, Math.max(min, value + (button.dataset.quantityPlus != null ? step : -step)));
      input.dispatchEvent(new Event('change', {bubbles: true}));
    });
  });



  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const initRecentlyViewed = () => {
    const dataNode = qs('[data-current-product-card]');
    if (!dataNode) return;
    let current;
    try { current = JSON.parse(dataNode.textContent); } catch (_) { return; }
    if (!current?.handle) return;
    const storageKey = 'frostpeak:recently-viewed';
    let products = [];
    try { products = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch (_) { products = []; }
    products = products.filter((item) => item?.handle && item.handle !== current.handle);

    const section = qs('[data-recently-viewed-section]');
    const grid = qs('[data-recently-viewed-grid]', section || document);
    const limit = Number(dataNode.dataset.limit || 4) || 4;
    const previous = products.slice(0, limit);
    if (section && grid && previous.length) {
      grid.innerHTML = previous.map((item) => {
        const price = formatMoney(Number(item.price || 0), window.FrostPeak.moneyFormat);
        const compare = Number(item.compare_at_price || 0) > Number(item.price || 0)
          ? `<s>${formatMoney(Number(item.compare_at_price), window.FrostPeak.moneyFormat)}</s>` : '';
        const image = item.image
          ? `<img src="${escapeHTML(item.image)}" loading="lazy" alt="${escapeHTML(item.title)}" width="720" height="900">`
          : '<div class="recent-card__placeholder"></div>';
        const badge = item.available === false ? `<span class="badge badge--dark">${escapeHTML(document.documentElement.lang.startsWith('fr') ? 'Épuisé' : 'Sold out')}</span>` : '';
        return `<article class="product-card recent-product-card">
          <a class="product-card__media ratio ratio--portrait" href="${escapeHTML(item.url)}" aria-label="${escapeHTML(item.title)}">${image}<div class="product-card__badges">${badge}</div></a>
          <div class="product-card__content">
            ${item.vendor ? `<div class="product-card__vendor">${escapeHTML(item.vendor)}</div>` : ''}
            <h3 class="product-card__title"><a href="${escapeHTML(item.url)}">${escapeHTML(item.title)}</a></h3>
            <div class="price"><span>${price}</span>${compare}</div>
          </div>
        </article>`;
      }).join('');
      section.hidden = false;
    }
    products.unshift(current);
    try { localStorage.setItem(storageKey, JSON.stringify(products.slice(0, 12))); } catch (_) {}
  };


  const initCompactStickyHeader = () => {
    const header = qs('[data-site-header]');
    if (!header) return;

    let scheduled = false;

    const update = () => {
      const shouldCompact = window.scrollY > 72;
      header.classList.toggle('is-scrolled', shouldCompact);
      document.documentElement.style.setProperty(
        '--frostpeak-header-height',
        `${Math.ceil(header.getBoundingClientRect().height)}px`
      );
      scheduled = false;
    };

    const requestUpdate = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, {passive: true});
    window.addEventListener('resize', requestUpdate);
    document.addEventListener('shopify:section:load', requestUpdate);
    update();
  };


  const initCatalogFilters = (scope = document) => {
    const bindFilterForm = (form) => {
      if (form.dataset.frostpeakFiltersReady === 'true') return;
      form.dataset.frostpeakFiltersReady = 'true';

      const autoSubmit = form.dataset.autoSubmit === 'true';
      const section = form.closest('[data-catalog-section]');
      let submitTimer;

      const markLoading = () => section?.classList.add('is-filtering');
      const submitForm = (delay = 0) => {
        clearTimeout(submitTimer);
        submitTimer = setTimeout(() => {
          markLoading();
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        }, delay);
      };

      form.addEventListener('change', (event) => {
        const target = event.target;
        const isSort = target.matches('select[data-sort-select]');
        const isFilterControl = target.matches(
          'input[type="checkbox"], input[type="radio"], input[type="number"], select:not([data-sort-select])'
        );

        if (!isSort && !isFilterControl) return;
        if (!autoSubmit && !isSort) return;
        submitForm(isSort ? 0 : 180);
      });

      form.addEventListener('submit', markLoading);
    };

    qsa('[data-facet-form]', scope).forEach(bindFilterForm);

    const savedView = (() => {
      try { return localStorage.getItem('frostpeak:catalog-view') || 'grid'; }
      catch (_) { return 'grid'; }
    })();

    qsa('[data-catalog-section]', scope).forEach((section) => {
      const grid = qs('[data-catalog-grid]', section);
      if (!grid) return;
      grid.classList.toggle('is-list-view', savedView === 'list');
      qsa('[data-catalog-view]', section).forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.catalogView === savedView));
      });
    });
  };


  const initCollectionSearch = () => {
    qsa('[data-collection-search]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        const input = qs('input[name="q"]', form);
        const term = input?.value.trim() || '';
        const base = form.dataset.baseQuery?.trim() || '';

        if (!base) return;

        event.preventDefault();

        const url = new URL(form.action, window.location.origin);
        const query = [term, base].filter(Boolean).join(' AND ');

        url.searchParams.set('q', query);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('options[unavailable_products]', 'last');

        window.location.assign(url.toString());
      });
    });
  };

  initCollectionSearch();

  initCatalogFilters();

  document.addEventListener('shopify:section:load', (event) => initCatalogFilters(event.target));
  window.addEventListener('pageshow', () => {
    qsa('[data-catalog-section]').forEach((section) => section.classList.remove('is-filtering'));
  });

  initCompactStickyHeader();

  initRecentlyViewed();

  window.FrostPeakTheme = {toast, formatMoney, openDrawer, closeDrawer};
})();
